import { extractHtmlBySelector, extractShopifyHtml, parseHTML } from "./html";
import { mergeQueryEntries, parseQueryInput, parseQueryString } from "./query";
import { FrameConfig, HtmlResolution, RemoteFrameRefreshPayload, ScrollSnapshot } from "./types";
import { escapeCssIdentifier, isElementScrollable } from "./utils";

/**
 * Resolves final renderable HTML from raw response text using frame rules.
 *
 * @param frame - Remote-frame host element.
 * @param raw - Raw response body.
 * @param config - Active resolved frame config.
 * @returns Resolved HTML and selector match metadata.
 */
export function resolveRenderableHtml(
  frame: HTMLElement,
  raw: string,
  config: FrameConfig
): HtmlResolution {
  if (config.shopify) {
    const shopifyHtml = extractShopifyHtml(raw, config.sectionId);
    const resolved = extractBySelectorOrFallback(frame, shopifyHtml, config.selector, config.select, true);
    return {
      html: unwrapSelfRemoteFrameShell(frame, resolved.html),
      matchedSelector: resolved.matchedSelector,
    };
  }

  if (config.selector) {
    const selected = extractHtmlBySelector(raw, config.selector, config.select);
    return {
      html: selected ?? "",
      matchedSelector: selected === null ? null : config.selector,
    };
  }

  const doc = parseHTML(raw);
  return {
    html: doc.body ? doc.body.innerHTML : raw,
    matchedSelector: null,
  };
}

/**
 * Extracts HTML by selector with optional own-id fallback.
 *
 * @param frame - Remote-frame host element.
 * @param html - Source HTML payload.
 * @param selector - Optional explicit selector.
 * @param select - Extraction mode.
 * @param preferOwnId - Whether the frame id should be used as fallback selector.
 * @returns Resolved HTML and match metadata.
 */
function extractBySelectorOrFallback(
  frame: HTMLElement,
  html: string,
  selector: string,
  select: FrameConfig["select"],
  preferOwnId: boolean
): HtmlResolution {
  if (selector) {
    const selected = extractHtmlBySelector(html, selector, select);
    return {
      html: selected ?? "",
      matchedSelector: selected === null ? null : selector,
    };
  }

  if (preferOwnId && frame.id) {
    const idSelector = `#${escapeCssIdentifier(frame.id)}`;
    const selectedById = extractHtmlBySelector(html, idSelector, "outer");
    if (selectedById !== null) {
      return {
        html: selectedById,
        matchedSelector: idSelector,
      };
    }
  }

  return {
    html,
    matchedSelector: null,
  };
}

/**
 * Removes a wrapping self `<remote-frame>` shell and returns child HTML.
 *
 * @param frame - Remote-frame host element.
 * @param html - Candidate HTML payload.
 * @returns Unwrapped HTML when shell conditions match, otherwise original HTML.
 */
function unwrapSelfRemoteFrameShell(frame: HTMLElement, html: string): string {
  if (!frame.id || !html.trim()) return html;

  const doc = parseHTML(html);
  const body = doc.body;
  if (!body) return html;

  let elementCount = 0;
  let onlyElement: Element | null = null;

  for (let i = 0; i < body.childNodes.length; i++) {
    const node = body.childNodes[i];
    if (node.nodeType === Node.ELEMENT_NODE) {
      elementCount += 1;
      if (elementCount > 1) return html;
      onlyElement = node as Element;
      continue;
    }

    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      return html;
    }
  }

  if (!onlyElement) return html;
  if (onlyElement.tagName.toLowerCase() !== "remote-frame") return html;
  if (onlyElement.getAttribute("id") !== frame.id) return html;

  return onlyElement.innerHTML;
}

/**
 * Builds the outbound request URL from base source and query merge/replace rules.
 *
 * @param config - Active resolved frame config.
 * @param payload - Optional per-refresh overrides.
 * @returns Resolved request URL, or `null` when no valid source is available.
 */
export function resolveRequestUrl(
  config: FrameConfig,
  payload: RemoteFrameRefreshPayload
): string | null {
  const payloadSrc = (payload.src ?? "").trim();
  const src = (payload.src ?? config.src).trim();

  if (config.urlBase === "src" && !src) return null;

  const baseUrl = (() => {
    if (config.urlBase === "current") {
      // In current mode, preserve live route query unless caller explicitly overrides src in payload.
      return new URL(payloadSrc || window.location.href, window.location.href);
    }
    return new URL(src, window.location.href);
  })();

  const queryFromAttr = parseQueryString(config.query);
  const queryFromPayload = parseQueryInput(payload.query);

  const mode = payload.queryMode ?? config.queryMode;
  const finalQuery =
    mode === "replace"
      ? queryFromPayload ?? queryFromAttr
      : mergeQueryEntries(queryFromAttr, queryFromPayload ?? []);

  if (mode === "replace") {
    baseUrl.search = "";
  }

  for (let i = 0; i < finalQuery.length; i++) {
    const [key, value] = finalQuery[i];
    if (value === null) {
      baseUrl.searchParams.delete(key);
    } else {
      baseUrl.searchParams.set(key, value);
    }
  }

  ensureShopifySectionRequestParam(baseUrl, config);

  return baseUrl.toString();
}

/**
 * Synchronizes browser history URL with the resolved request URL.
 *
 * @param requestUrl - Resolved request URL used for refresh.
 * @param config - Active resolved frame config.
 */
export function syncHistoryUrl(requestUrl: string, config: FrameConfig): void {
  if (config.history === "none") return;

  const current = new URL(window.location.href);
  const next = new URL(window.location.href);
  const request = new URL(requestUrl, window.location.href);

  if (config.historyScope === "full") {
    if (request.origin === current.origin) {
      next.pathname = request.pathname;
      next.hash = request.hash;
    }
  }

  if (config.historyParams.length) {
    syncHistoryParams(next, request, config.historyParams);
  } else {
    next.search = request.search;
    stripDefaultShopifyHistoryParams(next, config);
  }

  if (next.toString() === current.toString()) return;

  if (config.history === "replace") {
    window.history.replaceState(window.history.state, "", next.toString());
    return;
  }

  window.history.pushState(window.history.state, "", next.toString());
}

/**
 * Applies whitelisted query param updates from request URL onto history URL.
 *
 * @param next - History URL being mutated.
 * @param request - Request URL source for query values.
 * @param keys - Query keys allowed to sync.
 */
function syncHistoryParams(next: URL, request: URL, keys: string[]): void {
  const matches = createHistoryParamMatcher(keys);
  const matchedRequest = new Map<string, string[]>();
  const matchedNextKeys = new Set<string>();

  request.searchParams.forEach((value, key) => {
    if (!matches(key)) return;
    const values = matchedRequest.get(key);
    if (values) {
      values.push(value);
      return;
    }
    matchedRequest.set(key, [value]);
  });

  next.searchParams.forEach((_, key) => {
    if (!matches(key)) return;
    matchedNextKeys.add(key);
  });

  matchedNextKeys.forEach((key) => {
    next.searchParams.delete(key);
  });

  matchedRequest.forEach((values, key) => {
    for (let i = 0; i < values.length; i++) {
      next.searchParams.append(key, values[i]);
    }
  });
}

/**
 * Creates a matcher for history param allowlist tokens.
 *
 * Supported tokens:
 * - exact key: `variant`
 * - prefix wildcard: `filters.*` (matches keys starting with `filters.`)
 * - full wildcard: `*` (matches all keys)
 *
 * @param keys - History allowlist tokens.
 * @returns Predicate that checks whether a query key is allowed.
 */
function createHistoryParamMatcher(keys: string[]): (key: string) => boolean {
  const exact = new Set<string>();
  const prefixes: string[] = [];
  let matchAll = false;

  for (let i = 0; i < keys.length; i++) {
    const token = keys[i];
    if (!token) continue;

    if (token === "*") {
      matchAll = true;
      break;
    }

    if (token.endsWith("*")) {
      const prefix = token.slice(0, -1);
      if (!prefix) {
        matchAll = true;
        break;
      }
      prefixes.push(prefix);
      continue;
    }

    exact.add(token);
  }

  if (matchAll) {
    return () => true;
  }

  if (!exact.size && prefixes.length === 0) {
    return () => false;
  }

  return (key: string) => exact.has(key) || prefixes.some((prefix) => key.startsWith(prefix));
}

/**
 * Ensures Shopify section rendering requests include a section identifier.
 *
 * When `shopify` mode is enabled and request URL has neither `section_id` nor
 * `sections`, this helper attempts to add `section_id` from config:
 * - first: explicit `section-id`
 * - fallback: selector like `#shopify-section-main-product`
 *
 * @param url - Request URL being constructed.
 * @param config - Active resolved frame config.
 */
function ensureShopifySectionRequestParam(url: URL, config: FrameConfig): void {
  if (!config.shopify) return;
  if (url.searchParams.has("section_id") || url.searchParams.has("sections")) return;

  const sectionId = resolveShopifySectionId(config);
  if (!sectionId) return;

  url.searchParams.set("section_id", sectionId);
}

/**
 * Removes internal Shopify section params from browser history URL by default.
 *
 * These params are request mechanics and should not pollute storefront URLs
 * unless callers explicitly opt in through `history-params`.
 *
 * @param url - History URL being mutated.
 * @param config - Active resolved frame config.
 */
function stripDefaultShopifyHistoryParams(url: URL, config: FrameConfig): void {
  if (!config.shopify) return;
  url.searchParams.delete("section_id");
  url.searchParams.delete("sections");
}

/**
 * Resolves the section id to use for Shopify section rendering requests.
 *
 * @param config - Active resolved frame config.
 * @returns Section id, or empty string when unavailable.
 */
function resolveShopifySectionId(config: FrameConfig): string {
  if (config.sectionId) return config.sectionId;

  const selectorMatch = config.selector.match(/^#shopify-section-([A-Za-z0-9_-]+)$/);
  if (selectorMatch && selectorMatch[1]) {
    return selectorMatch[1];
  }

  return "";
}

/**
 * Captures host and keyed descendant scroll positions for restoration after patching.
 *
 * @param frame - Remote-frame host element.
 * @param config - Active resolved frame config.
 * @returns Scroll snapshot or `null` when scroll preservation is disabled.
 */
export function captureScrollState(frame: HTMLElement, config: FrameConfig): ScrollSnapshot | null {
  if (!config.preserveState.has("scroll")) return null;

  const snapshot: ScrollSnapshot = {
    host: {
      left: frame.scrollLeft,
      top: frame.scrollTop,
    },
    keyed: [],
  };

  const withIds = frame.querySelectorAll("[id]");
  for (let i = 0; i < withIds.length; i++) {
    const el = withIds[i] as HTMLElement;
    if (!isElementScrollable(el)) continue;

    snapshot.keyed.push({
      id: el.id,
      left: el.scrollLeft,
      top: el.scrollTop,
    });
  }

  return snapshot;
}

/**
 * Restores captured scroll positions after DOM patching.
 *
 * @param frame - Remote-frame host element.
 * @param snapshot - Previously captured scroll snapshot.
 * @param config - Active resolved frame config.
 */
export function restoreScrollState(
  frame: HTMLElement,
  snapshot: ScrollSnapshot | null,
  config: FrameConfig
): void {
  if (!snapshot || !config.preserveState.has("scroll")) return;

  if (typeof frame.scrollTo === "function") {
    frame.scrollTo(snapshot.host.left, snapshot.host.top);
  } else {
    frame.scrollLeft = snapshot.host.left;
    frame.scrollTop = snapshot.host.top;
  }

  for (let i = 0; i < snapshot.keyed.length; i++) {
    const state = snapshot.keyed[i];
    const match = frame.querySelector<HTMLElement>(`#${escapeCssIdentifier(state.id)}`);
    if (!match) continue;
    match.scrollLeft = state.left;
    match.scrollTop = state.top;
  }
}
